/**
 * Tax · Sprint F · #4
 *
 * Página unificada de modelos AEAT españoles para SaaS de talleres.
 * - Trimestrales: 303 (IVA), 111 (IRPF profesionales), 115 (IRPF alquiler), 130 (pago fraccionado autónomo)
 * - Anuales: 390 (resumen 303), 190 (resumen 111), 180 (resumen 115)
 *
 * Cada panel carga el JSON del endpoint, lo renderiza con sus casillas oficiales y
 * permite exportar JSON/CSV/PDF (PDF solo donde está disponible).
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, BACKEND_URL, formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { FilePdf, FileCsv, Receipt, ArrowDown, ArrowUp, Users, Buildings, CalendarBlank, Calculator, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const quarters = [
  { value: 1, label: "1T (Ene – Mar)" },
  { value: 2, label: "2T (Abr – Jun)" },
  { value: 3, label: "3T (Jul – Sep)" },
  { value: 4, label: "4T (Oct – Dic)" },
];

function fmt(n) {
  return new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));
}

function downloadAuthed(path, filename) {
  fetch(`${BACKEND_URL}${path}`, { credentials: "include" })
    .then((r) => r.blob())
    .then((b) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b);
      a.download = filename;
      a.click();
    })
    .catch(() => toast.error("No se pudo descargar"));
}

function downloadJson(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

function PeriodSelector({ year, setYear, quarter, setQuarter, annual, actions, testidPrefix }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-5 grid grid-cols-1 md:grid-cols-4 gap-4">
      <div>
        <Label className="text-xs uppercase tracking-wider">Año</Label>
        <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))}
               className="mt-1 rounded-sm tabular-nums" data-testid={`${testidPrefix}-year`} />
      </div>
      {!annual && (
        <div>
          <Label className="text-xs uppercase tracking-wider">Trimestre</Label>
          <Select value={String(quarter)} onValueChange={(v) => setQuarter(Number(v))}>
            <SelectTrigger className="mt-1 rounded-sm" data-testid={`${testidPrefix}-quarter`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {quarters.map((q) => <SelectItem key={q.value} value={String(q.value)}>{q.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className={`${annual ? "md:col-span-3" : "md:col-span-2"} flex items-end gap-2 justify-end flex-wrap`}>{actions}</div>
    </div>
  );
}

function StateBadge({ state, intent }) {
  const colorIntent = intent === "danger" ? "bg-red-200 text-red-900 hover:bg-red-200"
                   : intent === "ok" ? "bg-emerald-200 text-emerald-900 hover:bg-emerald-200"
                   : "bg-gray-200 text-gray-900 hover:bg-gray-200";
  return <Badge className={`rounded-sm ${colorIntent}`}>{state}</Badge>;
}

function CasillasTable({ title, period, rows, highlightFrom = -1 }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
      <div className="p-4 border-b border-gray-200 text-sm text-gray-700"><b>{title}</b> · {period}</div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-gray-500 w-20">Casilla</th>
            <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-gray-500">Concepto</th>
            <th className="text-right px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-gray-500 w-40">Valor</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={`${row[0]}-${i}`} className={`border-t border-gray-100 ${highlightFrom >= 0 && i >= highlightFrom ? "bg-gray-50 font-medium" : ""}`}>
              <td className="px-4 py-2 font-mono-tab text-xs">{row[0]}</td>
              <td className="px-4 py-2">{row[1]}</td>
              <td className="px-4 py-2 text-right tabular-nums">{row[2]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ===========================================================================
// Modelo 303 · IVA trimestral
// ===========================================================================
function M303Panel() {
  const now = new Date();
  const currentQ = Math.floor(now.getMonth() / 3) + 1;
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(currentQ);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/tax/m303?year=${year}&quarter=${quarter}`); setData(r.data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, quarter]);

  const bd = (k) => data?.buckets_devengado?.[k] || { base: 0, cuota: 0 };
  return (
    <div className="space-y-6 mt-4">
      <PeriodSelector year={year} setYear={setYear} quarter={quarter} setQuarter={setQuarter} testidPrefix="m303"
        actions={<>
          <Button variant="outline" className="rounded-sm" onClick={() => downloadAuthed(`/api/tax/m303/pdf?year=${year}&quarter=${quarter}`, `modelo303-${year}-T${quarter}.pdf`)} data-testid="m303-pdf-btn"><FilePdf className="mr-1.5" /> PDF</Button>
          <Button variant="outline" className="rounded-sm" onClick={() => downloadAuthed(`/api/tax/m303/csv?year=${year}&quarter=${quarter}`, `modelo303-${year}-T${quarter}.csv`)} data-testid="m303-csv-btn"><FileCsv className="mr-1.5" /> CSV</Button>
          <Button variant="outline" className="rounded-sm" onClick={() => downloadJson(data, `modelo303-${data?.period_label}.json`)} disabled={!data} data-testid="m303-json-btn"><Receipt className="mr-1.5" /> JSON</Button>
          <SendToGestorButton model="303" year={year} quarter={quarter} period={`${year}-T${quarter}`} />
        </>} />
      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="metric-card p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2"><ArrowUp size={12} /> IVA Devengado</div>
              <div className="font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 text-[var(--ag-gold)]">{fmt(data.iva_devengado)} €</div>
              <div className="text-xs text-gray-500 mt-2 tabular-nums">Base: {fmt(data.base_devengado)} € · {data.services_count} operaciones</div>
            </div>
            <div className="metric-card p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2"><ArrowDown size={12} /> IVA Soportado</div>
              <div className="font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 text-gray-700">{fmt(data.iva_soportado)} €</div>
              <div className="text-xs text-gray-500 mt-2 tabular-nums">Base: {fmt(data.base_soportado)} € · {data.expenses_count} gastos</div>
            </div>
            <div className={`metric-card p-6 border-2 ${data.resultado > 0 ? "border-red-300 bg-red-50" : data.resultado < 0 ? "border-emerald-300 bg-emerald-50" : "border-gray-300"}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-700">Resultado liquidación</div>
              <div className={`font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 ${data.resultado > 0 ? "text-red-700" : data.resultado < 0 ? "text-emerald-700" : "text-gray-900"}`}>{fmt(data.resultado)} €</div>
              <div className="mt-2"><StateBadge state={data.estado} intent={data.resultado > 0 ? "danger" : data.resultado < 0 ? "ok" : "neutral"} /></div>
            </div>
          </div>
          <CasillasTable
            title={`Casillas Modelo 303 · Periodo ${data.period_label}`}
            period={`${data.period_start} → ${data.period_end}`}
            rows={[
              ["01", "Base imponible IVA devengado 21%", fmt(bd("21").base) + " €"],
              ["03", "Cuota IVA devengada 21%", fmt(bd("21").cuota) + " €"],
              ["04", "Base imponible IVA devengado 10%", fmt(bd("10").base) + " €"],
              ["06", "Cuota IVA devengada 10%", fmt(bd("10").cuota) + " €"],
              ["07", "Base imponible IVA devengado 4%", fmt(bd("4").base) + " €"],
              ["09", "Cuota IVA devengada 4%", fmt(bd("4").cuota) + " €"],
              ["27", "TOTAL IVA DEVENGADO", fmt(data.iva_devengado) + " €"],
              ["28", "Base IVA soportado deducible", fmt(data.base_soportado) + " €"],
              ["29", "Cuota IVA soportado deducible", fmt(data.iva_soportado) + " €"],
              ["45", "Total a deducir", fmt(data.iva_soportado) + " €"],
              ["46", "Resultado régimen general", fmt(data.resultado) + " €"],
              ["69", `TOTAL (${data.estado})`, fmt(data.resultado) + " €"],
            ]}
            highlightFrom={10}
          />
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-xs text-amber-900" data-testid="m303-disclaimer">
            <b>Aviso:</b> Solo entran <b>servicios facturados (Pagado)</b>. Documento informativo. Presentación oficial: Sede Electrónica AEAT.
          </div>
        </>
      )}
      {loading && <div className="text-center text-gray-500 py-6">Cargando…</div>}
    </div>
  );
}

// ===========================================================================
// Modelo 111 · Retenciones IRPF profesionales/trabajadores
// ===========================================================================
function M111Panel() {
  const now = new Date();
  const currentQ = Math.floor(now.getMonth() / 3) + 1;
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(currentQ);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/tax/m111?year=${year}&quarter=${quarter}`); setData(r.data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, quarter]);

  return (
    <div className="space-y-6 mt-4">
      <PeriodSelector year={year} setYear={setYear} quarter={quarter} setQuarter={setQuarter} testidPrefix="m111"
        actions={<>
          <Button variant="outline" className="rounded-sm" onClick={() => downloadAuthed(`/api/tax/m111/pdf?year=${year}&quarter=${quarter}`, `modelo111-${year}-T${quarter}.pdf`)} data-testid="m111-pdf-btn"><FilePdf className="mr-1.5" /> PDF</Button>
          <Button variant="outline" className="rounded-sm" onClick={() => downloadAuthed(`/api/tax/m111/csv?year=${year}&quarter=${quarter}`, `modelo111-${year}-T${quarter}.csv`)} data-testid="m111-csv-btn"><FileCsv className="mr-1.5" /> CSV</Button>
          <Button variant="outline" className="rounded-sm" onClick={() => downloadJson(data, `modelo111-${data?.period_label}.json`)} disabled={!data} data-testid="m111-json-btn"><Receipt className="mr-1.5" /> JSON</Button>
          <SendToGestorButton model="111" year={year} quarter={quarter} period={`${year}-T${quarter}`} />
        </>} />
      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="metric-card p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2"><Users size={12} /> Profesionales (15%)</div>
              <div className="font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 text-[var(--ag-gold)]">{fmt(data.retenciones_profesionales)} €</div>
              <div className="text-xs text-gray-500 mt-2 tabular-nums">{data.casillas_111["04_n_perceptores_profesionales"]} perceptores · Base {fmt(data.base_profesional)} €</div>
            </div>
            <div className="metric-card p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2"><Users size={12} /> Trabajadores (nómina)</div>
              <div className="font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 text-gray-700">{fmt(data.retenciones_trabajo)} €</div>
              <div className="text-xs text-gray-500 mt-2 tabular-nums">{data.casillas_111["01_n_perceptores_trabajo"]} perceptores · Base {fmt(data.base_trabajo)} €</div>
            </div>
            <div className={`metric-card p-6 border-2 ${data.total_a_ingresar > 0 ? "border-red-300 bg-red-50" : "border-gray-300"}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-700">Total a ingresar</div>
              <div className={`font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 ${data.total_a_ingresar > 0 ? "text-red-700" : "text-gray-900"}`}>{fmt(data.total_a_ingresar)} €</div>
              <div className="mt-2"><StateBadge state={data.estado} intent={data.total_a_ingresar > 0 ? "danger" : "neutral"} /></div>
            </div>
          </div>
          {(data.perceptores_profesionales?.length > 0 || data.perceptores_trabajo?.length > 0) && (
            <PerceptoresTable
              title="Detalle perceptores"
              rows={[
                ...data.perceptores_trabajo.map((p) => ({ ...p, tipo: "Trabajo" })),
                ...data.perceptores_profesionales.map((p) => ({ ...p, tipo: "Profesional" })),
              ]}
            />
          )}
          <CasillasTable
            title={`Casillas Modelo 111 · Periodo ${data.period_label}`}
            period={`${data.period_start} → ${data.period_end}`}
            rows={[
              ["01", "Nº perceptores rendimientos del trabajo", String(data.casillas_111["01_n_perceptores_trabajo"])],
              ["02", "Base retenciones trabajo", fmt(data.base_trabajo) + " €"],
              ["03", "Retenciones e ingresos a cuenta trabajo", fmt(data.retenciones_trabajo) + " €"],
              ["04", "Nº perceptores rendimientos profesionales", String(data.casillas_111["04_n_perceptores_profesionales"])],
              ["05", "Base retenciones profesionales", fmt(data.base_profesional) + " €"],
              ["06", "Retenciones e ingresos a cuenta profesionales", fmt(data.retenciones_profesionales) + " €"],
              ["28", "Total liquidación trimestre", fmt(data.total_a_ingresar) + " €"],
              ["31", `TOTAL (${data.estado})`, fmt(data.total_a_ingresar) + " €"],
            ]}
            highlightFrom={6}
          />
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-xs text-amber-900" data-testid="m111-disclaimer">
            <b>Aviso:</b> El 111 incluye <b>retenciones a profesionales y trabajadores</b>. Las retenciones por alquiler urbano (19%) van al <b>Modelo 115</b> (pestaña aparte).
          </div>
        </>
      )}
      {loading && <div className="text-center text-gray-500 py-6">Cargando…</div>}
    </div>
  );
}

function PerceptoresTable({ title, rows }) {
  if (!rows?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
      <div className="p-4 border-b border-gray-200 text-sm text-gray-700"><b>{title}</b></div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-gray-500">Tipo</th>
            <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-gray-500">Nombre</th>
            <th className="text-right px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-gray-500">Base</th>
            <th className="text-right px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-gray-500">Retención</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => (
            <tr key={`${p.id}-${i}`} className="border-t border-gray-100">
              <td className="px-4 py-2 text-xs uppercase tracking-wider text-gray-500">{p.tipo || ""}</td>
              <td className="px-4 py-2">{p.name}</td>
              <td className="px-4 py-2 text-right tabular-nums">{fmt(p.base)} €</td>
              <td className="px-4 py-2 text-right tabular-nums">{fmt(p.retencion)} €</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Sprint I · #12 · Botón "Enviar a gestoría" para cada modelo AEAT
function SendToGestorButton({ model, year, quarter, period }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [override, setOverride] = useState("");
  const [busy, setBusy] = useState(false);
  const [defaultEmail, setDefaultEmail] = useState("");
  const [defaultName, setDefaultName] = useState("");

  const openDialog = async () => {
    try {
      const { data } = await api.get("/org/me");
      setDefaultEmail(data?.gestor_email || "");
      setDefaultName(data?.gestor_name || "");
    } catch {}
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = { model, year, quarter, note: note.trim() || null };
      if (override.trim()) body.to_override = override.trim();
      const { data } = await api.post("/tax/send-to-gestor", body);
      toast.success(`Enviado a ${data.to}`);
      setOpen(false);
      setNote("");
      setOverride("");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" className="rounded-sm bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
              onClick={openDialog} data-testid={`m${model}-send-gestor-btn`}>
        <EnvelopeSimple className="mr-1.5" weight="bold" /> Enviar a gestoría
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid={`send-gestor-dialog-${model}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <EnvelopeSimple weight="bold" className="text-emerald-600" /> Enviar Modelo {model} a tu gestoría
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 text-xs">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Destinatario por defecto</div>
              <div className="font-medium text-gray-900">
                {defaultEmail || <span className="text-amber-700">Sin configurar · ve a Ajustes → Gestoría</span>}
              </div>
              {defaultName && <div className="text-gray-600">{defaultName}</div>}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Email alternativo (opcional)</Label>
              <Input type="email" value={override} onChange={(e) => setOverride(e.target.value)}
                placeholder={defaultEmail || "gestor@gestoria.com"} className="mt-1 rounded-sm"
                data-testid={`send-gestor-override-${model}`} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Nota para el gestor (opcional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500}
                placeholder="Ej. Incluyo factura rectificativa del mes pasado…" className="mt-1 rounded-sm"
                data-testid={`send-gestor-note-${model}`} />
              <div className="text-[10px] text-[var(--ag-text-muted)] text-right">{note.length}/500</div>
            </div>
            <div className="text-[11px] text-gray-500 bg-blue-50 border border-blue-200 rounded-sm p-2.5">
              Se enviará CSV{["303","111"].includes(model) ? " + PDF informativo" : ""} con el resumen del periodo <b>{period}</b>.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid={`send-gestor-cancel-${model}`}>Cancelar</Button>
            <Button onClick={submit} disabled={busy || (!defaultEmail && !override.trim())}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`send-gestor-confirm-${model}`}>
              {busy ? "Enviando…" : "Enviar al gestor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Sprint I · #13 · Modal "Marcar 130 como presentado"
function Mark130FiledButton({ year, quarter, amountSuggested, onSaved }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(amountSuggested || 0);
  const [receipt, setReceipt] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const openDialog = () => { setAmount(amountSuggested || 0); setOpen(true); };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/tax/m130/mark-filed", {
        year, quarter, amount_paid: Number(amount), receipt_number: receipt.trim() || null, note: note.trim() || null,
      });
      toast.success(`Modelo 130 · ${year}-T${quarter} marcado como presentado`);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" className="rounded-sm border-purple-400 text-purple-700 hover:bg-purple-50"
              onClick={openDialog} data-testid="m130-mark-filed-btn">
        <CheckCircle className="mr-1.5" weight="bold" /> Marcar como presentado
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="mark-130-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CheckCircle weight="bold" className="text-purple-600" /> Marcar 130 como presentado
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-gray-600">
              Registra el pago real del Modelo 130 · {year}-T{quarter}. Esto sustituirá la
              estimación heurística por el importe exacto al calcular trimestres futuros.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wider">Importe pagado (€)</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="mt-1 rounded-sm tabular-nums" data-testid="mark-130-amount" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Nº de justificante (opcional)</Label>
              <Input value={receipt} onChange={(e) => setReceipt(e.target.value)}
                placeholder="Ej. NRC, código de operación AEAT…" className="mt-1 rounded-sm"
                data-testid="mark-130-receipt" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Nota (opcional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500}
                className="mt-1 rounded-sm" data-testid="mark-130-note" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancelar</Button>
            <Button onClick={submit} disabled={busy || !amount}
                    className="bg-purple-600 hover:bg-purple-700 text-[var(--ag-text)]"
                    data-testid="mark-130-confirm">
              {busy ? "Guardando…" : "Guardar presentación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ===========================================================================
// Modelo 115 · Retenciones IRPF alquiler inmueble urbano (trimestral)
// ===========================================================================
function M115Panel() {
  const now = new Date();
  const currentQ = Math.floor(now.getMonth() / 3) + 1;
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(currentQ);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/tax/m115?year=${year}&quarter=${quarter}`); setData(r.data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, quarter]);

  return (
    <div className="space-y-6 mt-4">
      <PeriodSelector year={year} setYear={setYear} quarter={quarter} setQuarter={setQuarter} testidPrefix="m115"
        actions={<>
          <Button variant="outline" className="rounded-sm" onClick={() => downloadAuthed(`/api/tax/m115/csv?year=${year}&quarter=${quarter}`, `modelo115-${year}-T${quarter}.csv`)} data-testid="m115-csv-btn"><FileCsv className="mr-1.5" /> CSV</Button>
          <Button variant="outline" className="rounded-sm" onClick={() => downloadJson(data, `modelo115-${data?.period_label}.json`)} disabled={!data} data-testid="m115-json-btn"><Receipt className="mr-1.5" /> JSON</Button>
          <SendToGestorButton model="115" year={year} quarter={quarter} period={`${year}-T${quarter}`} />
        </>} />
      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="metric-card p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2"><Buildings size={12} /> Arrendadores</div>
              <div className="font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 text-[var(--ag-gold)]">{data.n_perceptores}</div>
              <div className="text-xs text-gray-500 mt-2 tabular-nums">Inmuebles urbanos profesionales</div>
            </div>
            <div className="metric-card p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Base retenida</div>
              <div className="font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 text-gray-700">{fmt(data.base_total)} €</div>
              <div className="text-xs text-gray-500 mt-2 tabular-nums">Alquiler facturado del trimestre</div>
            </div>
            <div className={`metric-card p-6 border-2 ${data.total_a_ingresar > 0 ? "border-red-300 bg-red-50" : "border-gray-300"}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-700">Total a ingresar (19%)</div>
              <div className={`font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 ${data.total_a_ingresar > 0 ? "text-red-700" : "text-gray-900"}`}>{fmt(data.total_a_ingresar)} €</div>
              <div className="mt-2"><StateBadge state={data.estado} intent={data.total_a_ingresar > 0 ? "danger" : "neutral"} /></div>
            </div>
          </div>
          <PerceptoresTable title="Detalle arrendadores · Inmuebles urbanos" rows={data.perceptores.map((p) => ({ ...p, tipo: "Alquiler" }))} />
          <CasillasTable
            title={`Casillas Modelo 115 · Periodo ${data.period_label}`}
            period={`${data.period_start} → ${data.period_end}`}
            rows={[
              ["01", "Nº perceptores", String(data.casillas_115["01_n_perceptores"])],
              ["02", "Base retenciones (€)", fmt(data.base_total) + " €"],
              ["03", "Retenciones e ingresos a cuenta (€)", fmt(data.retenciones_total) + " €"],
              ["06", `TOTAL A INGRESAR (${data.estado})`, fmt(data.total_a_ingresar) + " €"],
            ]}
            highlightFrom={3}
          />
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-xs text-amber-900" data-testid="m115-disclaimer">
            <b>Aviso:</b> El 115 declara las retenciones del <b>alquiler del local del taller</b> (19% por arrendamiento de inmueble urbano profesional). Marca el gasto como "Alquiler inmueble urbano" en su clasificación fiscal para que aparezca aquí.
          </div>
        </>
      )}
      {loading && <div className="text-center text-gray-500 py-6">Cargando…</div>}
    </div>
  );
}

// ===========================================================================
// Modelo 130 · Pago fraccionado IRPF estimación directa (trimestral, autónomo)
// ===========================================================================
function M130Panel() {
  const now = new Date();
  const currentQ = Math.floor(now.getMonth() / 3) + 1;
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(currentQ);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/tax/m130?year=${year}&quarter=${quarter}`); setData(r.data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, quarter]);

  return (
    <div className="space-y-6 mt-4">
      <PeriodSelector year={year} setYear={setYear} quarter={quarter} setQuarter={setQuarter} testidPrefix="m130"
        actions={<>
          <Button variant="outline" className="rounded-sm" onClick={() => downloadAuthed(`/api/tax/m130/csv?year=${year}&quarter=${quarter}`, `modelo130-${year}-T${quarter}.csv`)} data-testid="m130-csv-btn"><FileCsv className="mr-1.5" /> CSV</Button>
          <Button variant="outline" className="rounded-sm" onClick={() => downloadJson(data, `modelo130-${data?.period_label}.json`)} disabled={!data} data-testid="m130-json-btn"><Receipt className="mr-1.5" /> JSON</Button>
          <SendToGestorButton model="130" year={year} quarter={quarter} period={`${year}-T${quarter}`} />
          <Mark130FiledButton year={year} quarter={quarter} amountSuggested={data?.resultado || 0} onSaved={load} />
        </>} />
      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="metric-card p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2"><ArrowUp size={12} /> Ingresos YTD</div>
              <div className="font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 text-[var(--ag-gold)]">{fmt(data.ingresos)} €</div>
              <div className="text-xs text-gray-500 mt-2 tabular-nums">Año a fecha · {data.period_start}</div>
            </div>
            <div className="metric-card p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2"><ArrowDown size={12} /> Gastos deducibles</div>
              <div className="font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 text-gray-700">{fmt(data.gastos)} €</div>
              <div className="text-xs text-gray-500 mt-2 tabular-nums">Acumulado YTD</div>
            </div>
            <div className={`metric-card p-6 border-2 ${data.resultado > 0 ? "border-red-300 bg-red-50" : "border-gray-300"}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-700">Pago fraccionado a ingresar</div>
              <div className={`font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 ${data.resultado > 0 ? "text-red-700" : "text-gray-900"}`}>{fmt(data.resultado)} €</div>
              <div className="mt-2"><StateBadge state={data.estado} intent={data.resultado > 0 ? "danger" : "neutral"} /></div>
            </div>
          </div>
          <CasillasTable
            title={`Casillas Modelo 130 · Periodo ${data.period_label}`}
            period={`${data.period_start} → ${data.period_end}`}
            rows={[
              ["01", "Ingresos computables (YTD)", fmt(data.ingresos) + " €"],
              ["02", "Gastos deducibles (YTD)", fmt(data.gastos) + " €"],
              ["03", "Rendimiento neto previo", fmt(data.rendimiento_neto) + " €"],
              ["04", "20% del rendimiento neto", fmt(data.pago_20pct) + " €"],
              ["05", "Pagos fraccionados de trimestres anteriores", fmt(data.pagos_fraccionados_previos) + " €"],
              ["06", "Retenciones soportadas (YTD)", fmt(data.retenciones_soportadas) + " €"],
              ["07", `RESULTADO A INGRESAR (${data.estado})`, fmt(data.resultado) + " €"],
            ]}
            highlightFrom={6}
          />
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-xs text-amber-900" data-testid="m130-disclaimer">
            <b>Aviso:</b> Aplicable si el titular tributa como <b>autónomo en estimación directa simplificada</b>. Si tu actividad es Sociedad Limitada (SL), no usas el 130 — usas el Modelo 202.
          </div>
        </>
      )}
      {loading && <div className="text-center text-gray-500 py-6">Cargando…</div>}
    </div>
  );
}

// ===========================================================================
// Modelos ANUALES: 390, 190, 180
// ===========================================================================
function AnnualPanel({ model, title, endpoint }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear() - 1); // default año anterior (presentación en enero)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`${endpoint}?year=${year}`); setData(r.data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const isM390 = model === "390";
  return (
    <div className="space-y-6 mt-4">
      <PeriodSelector annual year={year} setYear={setYear} testidPrefix={`m${model}`}
        actions={<>
          <Button variant="outline" className="rounded-sm" onClick={() => downloadAuthed(`${endpoint}/csv?year=${year}`, `modelo${model}-${year}.csv`)} data-testid={`m${model}-csv-btn`}><FileCsv className="mr-1.5" /> CSV</Button>
          <Button variant="outline" className="rounded-sm" onClick={() => downloadJson(data, `modelo${model}-${year}.json`)} disabled={!data} data-testid={`m${model}-json-btn`}><Receipt className="mr-1.5" /> JSON</Button>
          <SendToGestorButton model={model} year={year} quarter={null} period={String(year)} />
        </>} />
      {data && (
        <>
          {isM390 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="metric-card p-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2"><ArrowUp size={12} /> IVA Devengado anual</div>
                  <div className="font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 text-[var(--ag-gold)]">{fmt(data.iva_devengado_anual)} €</div>
                  <div className="text-xs text-gray-500 mt-2 tabular-nums">Base: {fmt(data.base_devengado_anual)} €</div>
                </div>
                <div className="metric-card p-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2"><ArrowDown size={12} /> IVA Soportado anual</div>
                  <div className="font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 text-gray-700">{fmt(data.iva_soportado_anual)} €</div>
                  <div className="text-xs text-gray-500 mt-2 tabular-nums">Base: {fmt(data.base_soportado_anual)} €</div>
                </div>
                <div className={`metric-card p-6 border-2 ${data.resultado_anual > 0 ? "border-red-300 bg-red-50" : data.resultado_anual < 0 ? "border-emerald-300 bg-emerald-50" : "border-gray-300"}`}>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-gray-700">Resultado anual</div>
                  <div className={`font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 ${data.resultado_anual > 0 ? "text-red-700" : data.resultado_anual < 0 ? "text-emerald-700" : "text-gray-900"}`}>{fmt(data.resultado_anual)} €</div>
                  <div className="mt-2"><StateBadge state={data.estado} intent={data.resultado_anual > 0 ? "danger" : data.resultado_anual < 0 ? "ok" : "neutral"} /></div>
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200 text-sm text-gray-700"><b>Desglose trimestral</b></div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-gray-500">Trimestre</th>
                      <th className="text-right px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-gray-500">Base devengado</th>
                      <th className="text-right px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-gray-500">IVA devengado</th>
                      <th className="text-right px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-gray-500">IVA soportado</th>
                      <th className="text-right px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-gray-500">Resultado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.trimestres.map((t) => (
                      <tr key={t.quarter} className="border-t border-gray-100">
                        <td className="px-4 py-2 font-mono-tab text-xs">T{t.quarter}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmt(t.base_devengado)} €</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmt(t.iva_devengado)} €</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmt(t.iva_soportado)} €</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmt(t.resultado)} €</td>
                      </tr>
                    ))}
                    <tr className="border-t border-gray-300 bg-gray-50 font-medium">
                      <td className="px-4 py-2 text-xs">TOTAL ANUAL</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmt(data.base_devengado_anual)} €</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmt(data.iva_devengado_anual)} €</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmt(data.iva_soportado_anual)} €</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmt(data.resultado_anual)} €</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="metric-card p-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Perceptores anuales</div>
                  <div className="font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 text-[var(--ag-gold)]">{data.n_perceptores}</div>
                </div>
                <div className="metric-card p-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Base total anual</div>
                  <div className="font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 text-gray-700">{fmt(data.total_base)} €</div>
                </div>
                <div className={`metric-card p-6 border-2 ${data.total_retenciones > 0 ? "border-red-300 bg-red-50" : "border-gray-300"}`}>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-gray-700">Total retenciones anuales</div>
                  <div className={`font-display font-bold text-4xl tracking-tighter tabular-nums mt-2 ${data.total_retenciones > 0 ? "text-red-700" : "text-gray-900"}`}>{fmt(data.total_retenciones)} €</div>
                  <div className="mt-2"><StateBadge state={data.estado} intent={data.total_retenciones > 0 ? "danger" : "neutral"} /></div>
                </div>
              </div>
              <PerceptoresTable title={`Detalle perceptores anuales · ${title}`} rows={data.perceptores.map((p) => ({ ...p, tipo: p.clave === "A" ? "Trabajo" : (p.clave === "G" ? "Profesional" : "—") }))} />
            </>
          )}
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-xs text-amber-900" data-testid={`m${model}-disclaimer`}>
            <b>Aviso:</b> Resumen anual informativo de los trimestres del año {data.year}. Presentación oficial: Sede Electrónica AEAT (enero).
          </div>
        </>
      )}
      {loading && <div className="text-center text-gray-500 py-6">Cargando…</div>}
    </div>
  );
}

// ===========================================================================
// Página principal
// ===========================================================================
export default function Tax() {
  const [searchParams] = useSearchParams();
  const initialModel = searchParams.get("model");
  const validModels = ["303", "111", "115", "130", "390", "190", "180"];
  const defaultTab = validModels.includes(initialModel) ? `m${initialModel}` : "m303";
  return (
    <div className="space-y-6">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Cumplimiento fiscal · AEAT</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1">Impuestos · Modelos</h1>
        <p className="text-sm text-gray-600 mt-2">Trimestrales: 303 · 111 · 115 · 130. Anuales: 390 · 190 · 180. Documentos informativos.</p>
      </header>
      <Tabs defaultValue={defaultTab} data-testid="tax-tabs">
        <TabsList className="rounded-sm flex-wrap h-auto">
          <TabsTrigger value="m303" className="rounded-sm" data-testid="tab-m303"><Calculator size={13} className="mr-1.5" /> 303 · IVA</TabsTrigger>
          <TabsTrigger value="m111" className="rounded-sm" data-testid="tab-m111"><Users size={13} className="mr-1.5" /> 111 · IRPF Prof.</TabsTrigger>
          <TabsTrigger value="m115" className="rounded-sm" data-testid="tab-m115"><Buildings size={13} className="mr-1.5" /> 115 · IRPF Alquiler</TabsTrigger>
          <TabsTrigger value="m130" className="rounded-sm" data-testid="tab-m130"><Calculator size={13} className="mr-1.5" /> 130 · Pago Frac.</TabsTrigger>
          <TabsTrigger value="m390" className="rounded-sm" data-testid="tab-m390"><CalendarBlank size={13} className="mr-1.5" /> 390 · Anual IVA</TabsTrigger>
          <TabsTrigger value="m190" className="rounded-sm" data-testid="tab-m190"><CalendarBlank size={13} className="mr-1.5" /> 190 · Anual 111</TabsTrigger>
          <TabsTrigger value="m180" className="rounded-sm" data-testid="tab-m180"><CalendarBlank size={13} className="mr-1.5" /> 180 · Anual 115</TabsTrigger>
        </TabsList>
        <TabsContent value="m303"><M303Panel /></TabsContent>
        <TabsContent value="m111"><M111Panel /></TabsContent>
        <TabsContent value="m115"><M115Panel /></TabsContent>
        <TabsContent value="m130"><M130Panel /></TabsContent>
        <TabsContent value="m390"><AnnualPanel model="390" title="Resumen anual del 303" endpoint="/tax/m390" /></TabsContent>
        <TabsContent value="m190"><AnnualPanel model="190" title="Resumen anual del 111" endpoint="/tax/m190" /></TabsContent>
        <TabsContent value="m180"><AnnualPanel model="180" title="Resumen anual del 115" endpoint="/tax/m180" /></TabsContent>
      </Tabs>
    </div>
  );
}
